import React, { useEffect, useState } from "react";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";

import { ProvisionSCCareOrgResponse } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import {
  ProvisionSCCareOrgParams,
  listSCCareTemplates,
  provisionSCCareOrg,
} from "api/endpoints/sc-care";
import { gRPCStatus } from "api/grpc-status-codes";
import { callOptionsHeaders } from "api/helpers";
import { AU_REGION_CODE, DEFAULT_REGION_CODE } from "constants/index";
import { GrpcError } from "context/snackbar-context";
import { isEmail2 } from "helpers/validator";

interface TemplateOption {
  id: string;
  name: string;
}

const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  AU: "AUD",
  US: "USD",
};

// SC Care orgs are provisioned into the cell matching the billing country, so
// all gRPC calls are routed by region derived from the selected country.
const COUNTRY_REGION_MAP: Record<string, string> = {
  AU: AU_REGION_CODE,
  US: DEFAULT_REGION_CODE,
};

const COUNTRY_OPTIONS = [
  { value: "AU", label: "Australia" },
  { value: "US", label: "United States" },
];

interface FormState {
  policyNumber: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  orgName: string;
  country: string;
  state: string;
  postalCode: string;
  currency: string;
  brokerEmail: string;
}

const initialFormState: FormState = {
  policyNumber: "",
  emailAddress: "",
  firstName: "",
  lastName: "",
  orgName: "",
  country: "",
  state: "",
  postalCode: "",
  currency: "",
  brokerEmail: "",
};

const ProvisionSCCareForm: React.FC = () => {
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ProvisionSCCareOrgResponse | null>(null);
  const [error, setError] = useState<GrpcError | undefined>(undefined);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [orgNameTouched, setOrgNameTouched] = useState(false);
  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<TemplateOption[]>(
    [],
  );
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesNotConfigured, setTemplatesNotConfigured] = useState(false);

  const region = COUNTRY_REGION_MAP[formState.country] ?? DEFAULT_REGION_CODE;

  useEffect(() => {
    setSelectedTemplates([]);
    setTemplateOptions([]);
    setTemplatesNotConfigured(false);

    if (!formState.country) {
      return;
    }

    // Guard against out-of-order responses when the country changes quickly:
    // only the latest request may update state.
    let active = true;
    const fetchTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const headers = callOptionsHeaders(
          COUNTRY_REGION_MAP[formState.country] ?? DEFAULT_REGION_CODE,
        );
        const resp = await listSCCareTemplates(headers);
        if (active) {
          setTemplateOptions(
            resp.templates.map((t) => ({ id: t.id, name: t.name })),
          );
        }
      } catch (e) {
        if (!active) {
          return;
        }
        if ((e as GrpcError).code === gRPCStatus.FAILED_PRECONDITION) {
          setTemplatesNotConfigured(true);
        } else {
          console.error("Failed to fetch SC Care templates:", e);
        }
      } finally {
        if (active) {
          setTemplatesLoading(false);
        }
      }
    };
    fetchTemplates();
    return () => {
      active = false;
    };
  }, [formState.country]);

  const isUS = formState.country === "US";

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const REQUIRED_FIELD_LABELS: Record<string, string> = {
    firstName: "First name",
    lastName: "Last name",
    emailAddress: "Email address",
    policyNumber: "Policy number",
    brokerEmail: "Broker email",
    country: "Country",
    state: "State",
    postalCode: "Postal code",
  };

  const handleBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    const trimmed = value.trim();

    // Required field check
    const label = REQUIRED_FIELD_LABELS[name];
    if (label && !trimmed) {
      // State and postal code only required for US
      if ((name === "state" || name === "postalCode") && !isUS) {
        return;
      }
      setValidationErrors((prev) => ({
        ...prev,
        [name]: `${label} is required`,
      }));
      return;
    }

    // Email format check
    if (
      (name === "emailAddress" || name === "brokerEmail") &&
      trimmed &&
      !isEmail2(trimmed)
    ) {
      setValidationErrors((prev) => ({ ...prev, [name]: "Email is invalid" }));
    }
  };

  const handleCountryChange = (country: string) => {
    const currency = COUNTRY_CURRENCY_MAP[country] || "";
    setFormState({
      ...formState,
      country,
      currency,
      state: "",
      postalCode: "",
    });
    if (
      validationErrors.country ||
      validationErrors.state ||
      validationErrors.postalCode
    ) {
      setValidationErrors((prev) => ({
        ...prev,
        country: "",
        state: "",
        postalCode: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formState.policyNumber.trim()) {
      errors.policyNumber = "Policy number is required";
    }
    if (!formState.emailAddress.trim()) {
      errors.emailAddress = "Email address is required";
    } else if (!isEmail2(formState.emailAddress.trim())) {
      errors.emailAddress = "Email is invalid";
    }
    if (!formState.brokerEmail.trim()) {
      errors.brokerEmail = "Broker email is required";
    } else if (!isEmail2(formState.brokerEmail.trim())) {
      errors.brokerEmail = "Email is invalid";
    }
    if (!formState.firstName.trim()) {
      errors.firstName = "First name is required";
    }
    if (!formState.lastName.trim()) {
      errors.lastName = "Last name is required";
    }
    if (!formState.country) {
      errors.country = "Country is required";
    }
    if (isUS) {
      if (!formState.state.trim()) {
        errors.state = "State is required for US";
      }
      if (!formState.postalCode.trim()) {
        errors.postalCode = "Postal code is required for US";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid =
    !!formState.firstName.trim() &&
    !!formState.lastName.trim() &&
    !!formState.emailAddress.trim() &&
    isEmail2(formState.emailAddress.trim()) &&
    !!formState.policyNumber.trim() &&
    !!formState.country &&
    (!isUS || (!!formState.state.trim() && !!formState.postalCode.trim())) &&
    !!formState.brokerEmail.trim() &&
    isEmail2(formState.brokerEmail.trim());

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setError(undefined);
    setIsProcessing(true);
    try {
      const headers = callOptionsHeaders(region);
      const params: ProvisionSCCareOrgParams = {
        policyNumber: formState.policyNumber.trim(),
        emailAddress: formState.emailAddress.trim(),
        firstName: formState.firstName.trim(),
        lastName: formState.lastName.trim(),
        orgName: formState.orgName.trim(),
        country: formState.country,
        state: formState.state.trim(),
        postalCode: formState.postalCode.trim(),
        currency: formState.currency,
        brokerEmail: formState.brokerEmail.trim(),
        sendWelcomeEmail: true,
        templateIds: selectedTemplates.map((t) => t.id),
      };
      const resp = await provisionSCCareOrg(params, headers);
      setResult(resp);
    } catch (e) {
      setError(e as GrpcError);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setFormState(initialFormState);
    setResult(null);
    setError(undefined);
    setValidationErrors({});
    setOrgNameTouched(false);
    setSelectedTemplates([]);
    setTemplateOptions([]);
    setTemplatesNotConfigured(false);
  };

  const handleCopy = (value: string, field: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const CopyButton = ({ value, field }: { value: string; field: string }) => (
    <Tooltip title={copiedField === field ? "Copied!" : "Copy"}>
      <IconButton
        size="small"
        onClick={() => handleCopy(value, field)}
        sx={{ ml: 0.5 }}
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  if (result) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          pt: 2,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 500,
            p: 3,
            borderRadius: 2,
            bgcolor: "background.paper",
            boxShadow: 1,
          }}
        >
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              SC Care Account Provisioned Successfully
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2">
                <strong>Org ID:</strong> {result.orgId}
              </Typography>
              <CopyButton value={result.orgId} field="orgId" />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2">
                <strong>User ID:</strong> {result.userId}
              </Typography>
              <CopyButton value={result.userId} field="userId" />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2">
                <strong>Email:</strong> {result.email}
              </Typography>
              <CopyButton value={result.email} field="email" />
            </Box>
            <Box sx={{ mt: 1 }}>
              <Link to={`/organisation/${region}/${result.orgId}`}>
                View Org in Periscope
              </Link>
            </Box>
          </Alert>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button variant="outlined" onClick={handleReset}>
              Provision Another Account
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        pt: 2,
      }}
    >
      <Box
        component="form"
        sx={{
          width: "100%",
          maxWidth: 500,
          p: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          boxShadow: 1,
        }}
        noValidate
        autoComplete="off"
      >
        <Typography variant="h6" gutterBottom align="center">
          Provision Policy Holder Account
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          align="center"
          sx={{ mb: 3 }}
        >
          Provision a new SC Care policy holder account with 3 free full seats.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              name="firstName"
              label="First Name"
              value={formState.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!validationErrors.firstName}
              helperText={validationErrors.firstName}
              disabled={isProcessing}
              size="small"
              data-testid="first-name-input"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              name="lastName"
              label="Last Name"
              value={formState.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!validationErrors.lastName}
              helperText={validationErrors.lastName}
              disabled={isProcessing}
              size="small"
              data-testid="last-name-input"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              name="emailAddress"
              label="Email Address"
              type="email"
              value={formState.emailAddress}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!validationErrors.emailAddress}
              helperText={validationErrors.emailAddress}
              disabled={isProcessing}
              size="small"
              data-testid="email-address-input"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="orgName"
              label="Organisation Name"
              value={formState.orgName}
              onChange={handleChange}
              onBlur={() => setOrgNameTouched(true)}
              disabled={isProcessing}
              size="small"
              helperText={
                orgNameTouched && !formState.orgName.trim()
                  ? "If left blank, this will be set to the customer's full name followed by 'Team'"
                  : undefined
              }
              data-testid="org-name-input"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              name="policyNumber"
              label="Policy Number"
              value={formState.policyNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!validationErrors.policyNumber}
              helperText={validationErrors.policyNumber}
              disabled={isProcessing}
              size="small"
              data-testid="policy-number-input"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              name="brokerEmail"
              label="Broker Email"
              type="email"
              value={formState.brokerEmail}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!validationErrors.brokerEmail}
              helperText={validationErrors.brokerEmail}
              disabled={isProcessing}
              size="small"
              data-testid="broker-email-input"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              required
              label="Country"
              value={formState.country}
              onChange={(e) => handleCountryChange(e.target.value)}
              error={!!validationErrors.country}
              helperText={validationErrors.country}
              disabled={isProcessing}
              size="small"
              data-testid="country-select"
            >
              {COUNTRY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Currency"
              value={formState.currency}
              disabled
              size="small"
              inputProps={{ style: { textAlign: "center" } }}
              data-testid="currency-select"
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={templateOptions}
              disableCloseOnSelect
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedTemplates}
              onChange={(_, newValue) => setSelectedTemplates(newValue)}
              loading={templatesLoading}
              disabled={
                isProcessing || !formState.country || templatesNotConfigured
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Templates"
                  size="small"
                  helperText={
                    !formState.country
                      ? "Select a country to load available templates."
                      : templatesNotConfigured
                        ? "No templates are configured for this region."
                        : "Optional. Templates to copy into the new organisation."
                  }
                  data-testid="templates-select"
                />
              )}
              data-testid="templates-autocomplete"
            />
          </Grid>
          {isUS && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  name="state"
                  label="State"
                  value={formState.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!validationErrors.state}
                  helperText={validationErrors.state}
                  disabled={isProcessing}
                  size="small"
                  data-testid="state-input"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  name="postalCode"
                  label="Postal Code"
                  value={formState.postalCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!validationErrors.postalCode}
                  helperText={validationErrors.postalCode}
                  disabled={isProcessing}
                  size="small"
                  data-testid="postal-code-input"
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Box
              sx={{ display: "flex", gap: 2, mt: 1, justifyContent: "center" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isProcessing || !isFormValid}
                sx={{ minWidth: 160 }}
                data-testid="submit-button"
              >
                {isProcessing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Provision Account"
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={isProcessing}
                sx={{ minWidth: 100 }}
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 3 }}
            onClose={() => setError(undefined)}
            data-testid="error-message"
          >
            {error.message}
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default ProvisionSCCareForm;
